import { cn } from "@corpuscomplete/web-ui/lib/utils";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { NutrientUI } from "@corpuscomplete/core-ui/nutrition/nutrient";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { NumericTargetProgress } from "../numeric-target-progress";
import { MoveRight } from "lucide-react";

/**
 * Compact nutrient progress bar set for current-vs-target display.
 * Contract: derives visible nutrient keys through core-ui display settings
 * and renders read-only progress without mutating nutrient policies.
 */
export function NutrientsProgress({
  className,
  fractionDigits = 0,
  targets,
  actual: _actual,
}: NutrientUI.NutrientsProgressProps) {
  const { nutrients } = NutrientUI.useDisplayNutrients();
  const actual = NutrientUI.nutrientValuesAsAmounts(_actual, nutrients);

  return (
    <div className={cn("flex w-full flex-row gap-1", className)}>
      {nutrients.map((nutrient) => {
        if (!nutrient.key) {
          return null;
        }
        const target = targets[nutrient.key];
        const actualAmount = actual[nutrient.key] ?? 0;
        const text = NutrientUI.nutrientLabel(nutrient, true);
        const formattedTarget = NutrientUI.formatTarget(
          target,
          nutrient.unit?.defaultName,
          fractionDigits,
        );
        if (!target || target.type === "max" || !formattedTarget) {
          return null;
        }
        return (
          <div key={nutrient.id} className="flex flex-1 flex-col gap-0.5">
            <Text variant="xs">
              {text}
              {actualAmount.toFixed(fractionDigits)}/{formattedTarget}
            </Text>
            <NumericTargetProgress value={actualAmount} target={target} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Detailed nutrient delta view for before/after comparisons.
 * Contract: accepts already-selected nutrient values and renders
 * read-only progress transitions without changing nutrient state.
 */
export function NutrientsProgressDetails({
  className,
  fractionDigits = 0,
  targets,
  base,
  delta,
}: {
  className?: string;
  fractionDigits?: number;
  targets: Nutrient.Targets;
  base: NutrientUI.NutrientValues;
  delta: NutrientUI.NutrientValues;
}) {
  const { nutrients } = NutrientUI.useDisplayNutrients();
  const baseAmounts = NutrientUI.nutrientValuesAsAmounts(base, nutrients);
  const deltaAmounts = NutrientUI.nutrientValuesAsAmounts(delta, nutrients);

  return (
    <div className={className}>
      <Text variant="h4">Nutrients Impact</Text>
      <div className="flex flex-col gap-2">
        {nutrients.map((nutrient) => {
          if (!nutrient.key) {
            return null;
          }
          const target = targets[nutrient.key];
          const baseAmount = baseAmounts[nutrient.key] ?? 0;
          const deltaAmount = deltaAmounts[nutrient.key] ?? 0;
          const totalAmount = baseAmount + deltaAmount;
          const text = NutrientUI.nutrientLabel(nutrient, false);
          const formattedTarget = NutrientUI.formatTarget(
            target,
            nutrient.unit?.defaultName,
            fractionDigits,
          );
          if (!target || target.type === "max" || !formattedTarget) {
            return null;
          }

          return (
            <div key={nutrient.id} className="flex flex-col gap-0.5">
              <div className="flex items-center justify-between">
                <Text variant="sm">{text}</Text>
                <div className="flex items-center gap-1">
                  <Text variant="sm">{baseAmount.toFixed(fractionDigits)}</Text>
                  <MoveRight className="size-4" />
                  <Text variant="sm">
                    {totalAmount.toFixed(fractionDigits)}/{formattedTarget}
                  </Text>
                </div>
              </div>
              <NumericTargetProgress value={baseAmount} target={target} />
              <NumericTargetProgress value={totalAmount} target={target} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
